import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis, type BarShapeProps } from "recharts";
import { Card, CardContent, Heading, Paragraph, Flex, Button, Skeleton, Division, Icons, Span, CardFooter, Chip } from "@ir/ui";
import { useAnalyticsKPI, useAnalyticsMobile } from "@/hooks";
import {
  ANALYTICS_CHECK_DATE_RANGE_LABEL,
  groupPurchasersWithOther,
  PurchaserChartItem,
} from "@/store/analytics";
import EmptyKpiCard from "./EmptyKpiCard";
import classNames from "classnames";
import "../../styles/Analytics.page.scss";

const CHART_COLORS = [
  "var(--primary-400)",
  "var(--secondary-400)",
  "var(--warning-400)",
  "var(--error-400)",
  "var(--primary-300)",
  "var(--success-500)",
  "var(--secondary-600)",
  "var(--success-300)",
];

const HEADING = "Deductions by Purchaser";

type ChartMetric = "amount" | "count";

type ChartItem = PurchaserChartItem & { fill: string };

const formatMetricValue = (value: number, metric: ChartMetric) => {
  if (metric === "count") {
    return value.toLocaleString();
  }

  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  })
    .format(value)
    .replace(/^/, "$");
};

const PurchaserTooltip = ({ active, payload, selectedOption }: { active?: boolean; payload?: { payload: ChartItem }[]; selectedOption: ChartMetric }) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;
  const getMetricRowStyle = (metric: ChartMetric) => ({
    color: selectedOption === metric ? "var(--white-color)" : "var(--grey-300)",
    fontWeight: selectedOption === metric ? 600 : 400,
  });

  return (
    <Division
      style={{
        background: "var(--grey-900)",
        borderRadius: 8,
        padding: "10px 14px",
        color: "var(--white-color)",
        fontSize: 13,
        minWidth: 180,
      }}
    >
      <Flex align="center" gap={.5} style={{ fontWeight: 600, marginBottom: 6 }}><Span className="d-inline-block" style={{ height: 15, width: 15, borderRadius: 4, backgroundColor: item.fill }}></Span> {item.purchaserName}</Flex>
      <Division style={{ marginBottom: 4, ...getMetricRowStyle("amount") }}>
        {formatMetricValue(item.amount, "amount")}{" "}
        <span style={{ color: "var(--grey-300)" }}>({item.share.toFixed(1)}% of total)</span>
      </Division>
      <Division style={getMetricRowStyle("count")}>
        Count: {item.count.toLocaleString()} {item.count === 1 ? "deduction" : "deductions"}
      </Division>
    </Division>
  );
};

const PurchaserBarShape = ({ x, y, width, height, radius, payload }: BarShapeProps) => {
  const fill = typeof payload?.fill === "string" ? payload.fill : "var(--grey-200)";

  return <Rectangle x={x} y={y} width={width} height={height} radius={radius} fill={fill} />;
};

const SKELETON_ROWS = [85, 65, 55, 45, 70, 40];

const DeductionsByPurchaserSkeleton = () => (
  <Division
    style={{
      padding: "var(--spacing-3)",
    }}
  >
    {SKELETON_ROWS.map((widthPercent) => (
      <Flex key={widthPercent} align="center" gap={2} style={{ marginBottom: widthPercent === SKELETON_ROWS.at(-1) ? 0 : 24 }}>
        <Skeleton width={72} height={12} style={{ borderRadius: 4, flexShrink: 0 }} />
        <div style={{ width: `${widthPercent}%` }}>
          <Skeleton height={20} style={{ borderRadius: 8, width: "100%" }} />
        </div>
      </Flex>
    ))}
    <Flex justifyContent="space-between" style={{ marginTop: 16 }}>
      {["kpi-1", "kpi-2", "kpi-3", "kpi-4", "kpi-5"].map(key => (
        <Skeleton key={key} width={36} height={10} style={{ borderRadius: 4 }} />
      ))}
    </Flex>
  </Division>
);

const DeductionsByPurchaserChart = ({
  selectedOption,
  items,
  isMobile,
}: {
  selectedOption: ChartMetric;
  items: ChartItem[];
  isMobile: boolean;
}) => {
  const chartData = items;

  if (chartData.length === 0) {
    return (
      <Flex justifyContent="center" align="center" style={{ height: "100%" }}>
        <EmptyKpiCard
        title="No data available"
        description="No deductions recorded for the selected period. Try adjusting the date range."
        icon={Icons.BarChart5}
      />
      </Flex>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 260 : 350}>
      <BarChart
        layout="vertical"
        barSize={20}
        data={chartData}
        margin={{
          top: 12,
          right: 12,
          left: 12,
          bottom: 8,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" accumulate="sum" startOffset="expand" stroke="var(--grey-200)" />
        <XAxis
          type="number"
          stroke="var(--grey-500)"
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: number) => formatMetricValue(value, selectedOption)}
        />
        <YAxis
          type="category"
          dataKey="purchaserName"
          width={isMobile ? 72 : 90}
          stroke="var(--grey-500)"
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<PurchaserTooltip selectedOption={selectedOption} />} cursor={{ fill: "transparent" }} />
        <Bar dataKey={selectedOption} radius={[0, 8, 8, 0]} barSize={20} shape={PurchaserBarShape} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const DeductionsByPurchaserCard = () => {
  const [selectedOption, setSelectedOption] = useState<ChartMetric>("amount");
  const { deductionsByPurchaser } = useAnalyticsKPI();
  const isMobile = useAnalyticsMobile();

  const chartItems = useMemo(() => {
    const grouped = groupPurchasersWithOther(deductionsByPurchaser.items, selectedOption);
    return grouped.map((item, index) => ({
      ...item,
      fill: item.isOther ? CHART_COLORS.at(-1)! : CHART_COLORS[index % (CHART_COLORS.length - 1)],
    }));
  }, [deductionsByPurchaser.items, selectedOption]);

  return (
    <Card className="bg-white border-1 border-color-grey-50">
      <Flex
        className="analytics-kpi-card__header px-4 py-3 border-bottom-1 border-color-grey-25"
        justifyContent="space-between"
        align="center"
      >
        <Flex direction="column">
          <Heading text={HEADING} level={6} className="m-0 text-grey-500 font-bold" />
          {!isMobile && (
            <Paragraph className="m-0 text-grey-400">{selectedOption === "amount" ? "Amount" : "Count"} of deductions by purchaser</Paragraph>
          )}
        </Flex>
        {!deductionsByPurchaser.isLoading && (
          <Flex gap={1} align="center">
            <Button.Group className="p-1 bg-grey-10 border-1 border-color-grey-100 rounded-small">
              <Button
                size="small"
                color="grey"
                variant={selectedOption === "amount" ? "solid" : "clear"}
                onPress={() => setSelectedOption("amount")}
                className={classNames("m-0", {
                  "bg-grey-25": selectedOption === "amount",
                  "text-grey-600": selectedOption === "amount",
                })}
              >
                Amount
              </Button>
              <Button
                size="small"
                color="grey"
                variant={selectedOption === "count" ? "solid" : "clear"}
                onPress={() => setSelectedOption("count")}
                className={classNames("m-0", {
                  "bg-grey-25": selectedOption === "count",
                  "text-grey-600": selectedOption === "count",
                })}
              >
                Count
              </Button>
            </Button.Group>
          </Flex>
        )}
      </Flex>
      <CardContent className="analytics-chart-content" style={{ height: isMobile ? 280 : 370 }}>
        {deductionsByPurchaser.isLoading ? (
          <DeductionsByPurchaserSkeleton />
        ) : (
          <DeductionsByPurchaserChart selectedOption={selectedOption} items={chartItems} isMobile={isMobile} />
        )}
      </CardContent>
      <CardFooter
        className="analytics-card-footer bg-grey-10 text-grey-500"
        style={{ borderBottomLeftRadius: ".75rem", borderBottomRightRadius: ".75rem" }}
      >
        <Flex justifyContent="space-between" align="center" gap={0.5} wrap="wrap">
          Filter applied:{" "}
          <Chip label={ANALYTICS_CHECK_DATE_RANGE_LABEL} variant="filled" color="secondary" borderless />
        </Flex>
      </CardFooter>
    </Card>
  );
};

export default DeductionsByPurchaserCard;
